import { useContext, useEffect, useState } from 'react';
import { OTHER_OPTION_ID } from '../constants';
import FormStructureContext from '../context/FormStructureContext';
import InputContext from '../context/InputContext';
import { SelectProps, SelectValue } from '../schema/interface';
import { SET_FIELD } from './reducer';
import useAlgorithm from './useAlgorithm';

export const useSingleSelect = ({
    name,
    restricted,
    items,
    separator,
    value,
    min,
    max,
    required,
    handleChangeVisible,
    formValues
}: SelectProps) => {
    const { dispatch } = useContext(InputContext);
    const [errorOtherOption, setErrorOtherOption] = useState(false);
    const [isOtherOptionEnabled, setIsOtherOptionEnabled] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<string[][]>([]);
    const [options, setOptions] = useState<string[][]>([]);
    const [otherOptionValue, setOtherOptionValue] = useState<string>('');
    const { fetchedFormStructure: formStructure } = useContext(FormStructureContext);
    const { handleAlgorithmCode } = useAlgorithm({
        formStructure,
        formValues,
        handleChangeVisible
    });

    useEffect(() => {
        if (items instanceof Array) {
            setOptions(items);
        } else {
            const listOfItems = items ? items.split(separator) : [];
            setOptions(listOfItems.map((val) => [val, val]));
        }
        if (restricted === false) {
            setOptions((options) => {
                const newList = [...options, [OTHER_OPTION_ID, OTHER_OPTION_ID]];
                return newList;
            });
        }
    }, [items]);

    useEffect(() => {
        if (restricted === false && value) {
            if (value instanceof Array) {
                if (value?.length) {
                    const found = value.find((val) => !options.find((option) => option[0] === val));
                    if (found != null) {
                        setOtherOptionValue(found);
                        setIsOtherOptionEnabled(true);
                    } else {
                        setOtherOptionValue('');
                        setIsOtherOptionEnabled(false);
                    }
                }
                // else if (selectedOptions.find(val => val[0] === OTHER_OPTION_ID)) {
                //   setIsOtherOptionEnabled(true);
                // }
            } else if ((value as SelectValue)?.others?.length) {
                setIsOtherOptionEnabled(true);
                setOtherOptionValue((value as SelectValue).others[0]?.toString());
            } else if (selectedOptions.find((val) => val[0] === OTHER_OPTION_ID)) {
                setIsOtherOptionEnabled(true);
            } else {
                setOtherOptionValue('');
                setIsOtherOptionEnabled(false);
            }
        } else {
            setOtherOptionValue('');
            setIsOtherOptionEnabled(false);
        }
    }, [value, options, restricted]);

    useEffect(() => {
        if (selectedOptions?.find?.((val) => val[0] === OTHER_OPTION_ID)) {
            setSelectedOptions([[OTHER_OPTION_ID, OTHER_OPTION_ID]]);
        } else if ((value as SelectValue)?.others?.length) {
            setSelectedOptions([[OTHER_OPTION_ID, OTHER_OPTION_ID]]);
        } else if ((value as SelectValue)?.choices?.length) {
            const mappedList = options.filter((val) =>
                typeof (value as SelectValue).choices[0] === 'string'
                    ? (value as SelectValue).choices[0] === val[0]
                    : (value as SelectValue).choices[0]?.includes(val[0])
            );
            setSelectedOptions(mappedList);
        } else if (value instanceof Array && value.length) {
            const found = options.find((option) => option[0] === value?.toString?.());
            if (!found) {
                setSelectedOptions([[OTHER_OPTION_ID, OTHER_OPTION_ID]]);
            } else if ((value as Array<string>)?.length) {
                setSelectedOptions((value as Array<string>).map((val) => [val, val]));
            }
        }
        return () => {
            setSelectedOptions([]);
        };
    }, [value, options]);

    // to uncheck the active radioButton which click on it

    const handleClick = (value: string) => {
        if (
            typeof value === 'string' &&
            selectedOptions?.length &&
            selectedOptions[0]?.includes(value)
        ) {
            setSelectedOptions([]);
            dispatch({
                type: SET_FIELD,
                name,
                value: {
                    choices: [],
                    others: []
                }
            });
        }
    };
    const handleSelect = (value: string | string[]) => {
        let selectedValue: string[] = [];
        if (typeof value === 'string') {
            selectedValue = [value];
        } else {
            selectedValue = value;
        }
        if (selectedValue?.includes?.(OTHER_OPTION_ID)) {
            setIsOtherOptionEnabled(true);
            dispatch({
                type: SET_FIELD,
                name,
                value: {
                    choices: [],
                    others: []
                }
            });
            setSelectedOptions([[OTHER_OPTION_ID, OTHER_OPTION_ID]]);
        } else {
            setSelectedOptions([]);
            setIsOtherOptionEnabled(false);
            dispatch({
                type: SET_FIELD,
                name,
                value: {
                    choices: selectedValue ? [...selectedValue] : [],
                    others: []
                }
            });
        }
    };
    const handleToggleOption = (option: string) => {
        const found = selectedOptions?.find?.((val) => val[0] === option);
        if (found) {
            handleSelect([]);
        } else {
            handleSelect(option);
        }
    };
    const handleInputChange = (e) => {
        const value = e.target.value;
        setErrorOtherOption(false);
        dispatch({
            type: SET_FIELD,
            name,
            value: {
                choices: [],
                others: [value]
            }
        });
    };
    const validateInput = () => {
        if (options.find((val) => val[1] === value?.toString())) {
            setErrorOtherOption(true);
        }
        if (value) {
            handleAlgorithmCode();
        }
    };
    const handleBlur = () => {
        if (value) {
            handleAlgorithmCode();
        }
    };
    return {
        selectedOptions,
        handleSelect,
        isOtherOptionEnabled,
        options,
        handleInputChange,
        handleClick,
        validateInput,
        errorOtherOption,
        name,
        value,
        min,
        max,
        required,
        otherOptionValue,
        handleToggleOption,
        handleBlur
    };
};
export default useSingleSelect;
